import axios from 'axios';

export type CountryOption = {
  countryCode: string;
  customizedLabel: string;
};

export type AirportByCountry = {
  airportCode: string;
  otherAirportCodes: string[];
  iataRegionCode: string;
  countryCode: string;
};

export type LocationPayload = {
  countryCode: string;
  airports: Array<{
    airportCode: string;
    otherAirportCodes: string[];
    iataRegionCode: string;
  }>;
};

const staticContentBase = () => (process.env.REACT_APP_STATIC_CONTENT || '').trim();

let cachedCountries: CountryOption[] | null = null;
let pendingCountries: Promise<CountryOption[]> | null = null;

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof value === 'string' && value.trim()) {
    return value.replace(/[{}]/g, '').split(',').map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

export const toLocationPayload = (
  countryCode: string,
  airports: AirportByCountry[] = []
): LocationPayload => ({
  countryCode,
  airports: airports.map((airport) => ({
    airportCode: airport.airportCode,
    otherAirportCodes: toStringArray(airport.otherAirportCodes),
    iataRegionCode: airport.iataRegionCode == null ? '' : String(airport.iataRegionCode),
  })),
});

export const getAirportCountries = async (): Promise<CountryOption[]> => {
  if (cachedCountries) {
    return cachedCountries;
  }
  if (pendingCountries) {
    return pendingCountries;
  }

  pendingCountries = (async () => {
    try {
      const response = await axios.get(`${staticContentBase()}/airportData/countries`);
      const countries = Array.isArray(response.data) ? response.data : [];
      const mapped = countries
        .filter((country: any) => country?.countryCode)
        .map((country: any) => {
          const countryCode = String(country.countryCode).toUpperCase();
          return {
            countryCode,
            customizedLabel: countryCode,
          };
        });
      cachedCountries = mapped;
      return mapped;
    } catch (error) {
      console.error('Failed to load country codes from airportData', error);
      return [];
    } finally {
      pendingCountries = null;
    }
  })();

  return pendingCountries;
};

export const getAirportsByCountry = async (countryCode: string): Promise<LocationPayload | null> => {
  if (!countryCode) {
    return null;
  }
  try {
    const response = await axios.get(
      `${staticContentBase()}/airportData/country/${encodeURIComponent(countryCode)}`
    );
    return toLocationPayload(
      countryCode.toUpperCase(),
      Array.isArray(response.data?.airports) ? response.data.airports : []
    );
  } catch (error) {
    console.error('Failed to load airports from airportData', error);
    return null;
  }
};
